import { DateSelectOption } from '@gamma/ui/form-fields';
import { ExtendedType } from '@gamma/ui/progress';
import { Auth } from 'aws-amplify';
import { safeParse } from 'secure-json-parse';
import { ROUTES } from './routes';

export enum EntityAnalysisModalTab {
  EntityOverview = 'entityOverview',
  EDRDetails = 'edrDetails',
}

let __jwtCache: { token: string; exp: number } | null = null;

const decodeExp = (jwt: string): number => {
  try {
    const [, payloadB64Url] = jwt.split('.');
    const b64 = payloadB64Url
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payloadB64Url.length / 4) * 4, '=');
    const json = safeParse(atob(b64));
    return Number(json.exp) || 0;
  } catch {
    return 0;
  }
};

const isExpiringSoon = (exp: number, skewSec = 60) => {
  const now = Math.floor(Date.now() / 1000);
  return exp === 0 || exp - now <= skewSec;
};

// Exported so other code (Humio) can use exactly the same recovery path
export const getIdTokenSafely = async (): Promise<string | null> => {
  // Check cache first
  if (__jwtCache && !isExpiringSoon(__jwtCache.exp)) {
    return __jwtCache.token;
  }

  const tryFetch = async () => {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      __jwtCache = { token, exp: decodeExp(token) };
      return token;
    } catch (error) {
      // Try to get from localStorage as fallback
      const storedToken = localStorage.getItem('docs_id_token');
      if (
        storedToken &&
        storedToken !== 'undefined' &&
        storedToken !== 'null'
      ) {
        try {
          const exp = decodeExp(storedToken);
          if (!isExpiringSoon(exp)) {
            __jwtCache = { token: storedToken, exp };

            return storedToken;
          }
        } catch (e) {
          console.warn('[JWT] Invalid token in localStorage:', e);
        }
      }
      throw error;
    }
  };

  try {
    return await tryFetch();
  } catch (err1) {
    console.warn('[JWT] currentSession failed; attempting recovery…', err1);
    try {
      // Force refresh the user to get new tokens
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      if (currentUser) {
        // Try once more after refresh
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        __jwtCache = { token, exp: decodeExp(token) };

        return token;
      }
    } catch (err2) {
      console.error('[JWT] Recovery failed', err2);

      // Last resort: check localStorage one more time
      const storedToken = localStorage.getItem('docs_id_token');
      if (
        storedToken &&
        storedToken !== 'undefined' &&
        storedToken !== 'null'
      ) {
        console.warn('[JWT] Using localStorage as last resort');
        return storedToken;
      }

      __jwtCache = null;
      return null;
    }
  }
  // Ensure all code paths return a value
  return null;
};

export const CONSTANTS = {
  FEDERATION_TYPE: {
    ORG: 'ORG',
    MEMBER: 'MEMBER',
    NONE: 'NONE',
  },
  TENANT_TYPE: {
    POV: 'POV',
    PRIME: 'PRIME',
    STANDARD: 'STANDARD',
  },
  ROLE: {
    admin: 'admin',
    analyst: 'analyst',
    viewer: 'viewer',
  },
  APPSYNC: {
    url: `${process.env.NX_PUBLIC_INVESTIGATOR_BASE_HREF ?? '/'}api/graphql`,
    region: 'us-west-2',
    auth: {
      type: 'AMAZON_COGNITO_USER_POOLS',
      jwtToken: async () => {
        // Wait up to ~1.5s for a token if it's not immediately ready
        const deadline = Date.now() + 1500;
        let token: string | null = null;

        while (!(token = await getIdTokenSafely()) && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 100));
        }

        // If still no token, intentionally throw to trigger your retry link,
        // instead of returning an empty string that causes guaranteed 401.
        if (!token) {
          throw new Error('No JWT available yet');
        }
        return token;
      },
    },
  },
  MFA_OPTIONS: {
    SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA',
  },
  MOCK_API: {
    URL: 'http://localhost:4000/graphql',
  },
  SSO: {
    domain:
      window.__env__?.sso.domain ??
      'develop-humio-server.auth.us-west-2.amazoncognito.com',
    client_id: window.__env__?.sso.client_id ?? '7akdknksa6nhbq4t2a18311m89',
    redirect_uri:
      window.__env__?.sso.redirect_uri ?? 'https://develop.ptrx.net/login',
    remote_manager_url: `urn:amazon:cognito:sp:${
      window.__env__?.cognito.cognito_user_pool ??
      process.env.REACT_APP_COGNITO_USER_POOL
    }`,
    remote_manager_sso_url: `https://${
      window.__env__?.sso.domain ??
      'develop-humio-server.auth.us-west-2.amazoncognito.com'
    }/saml2/idpresponse`,
  },
};

export const mapTenantType = (t?: string) => {
  if (t === CONSTANTS.TENANT_TYPE.PRIME) return 'PRIME' as const;
  if (t === CONSTANTS.TENANT_TYPE.POV) return 'POV' as const;
  // POC, DOC, and all other types map to STANDARD
  return 'STANDARD' as const;
};

export const humioDashboard = {
  sensor: 'Data - Corelight Health Report - Sensor Status',
  session_token_url:
    process.env.NX_PUBLIC_HUMIO_SESSION_TOKEN_URL ??
    '/search/api/v1/authenticate/token',
  pov_session_token_url:
    process.env.NX_PUBLIC_POV_HUMIO_SESSION_TOKEN_URL ??
    '/search-internal/api/v1/authenticate/token',
  prime_session_token_url: (tenantId: string) =>
    process.env.NX_PUBLIC_PRIME_HUMIO_SESSION_TOKEN_URL?.replace(
      '{tenantId}',
      tenantId,
    ) ?? `/search-${tenantId}/api/v1/authenticate/token`,
};

export enum graphqlTypeNames {
  user = 'User',
}

export enum USER_STATUS {
  active = 'active',
  inactive = 'inactive',
  invited = 'invited',
}

export enum MODAL_STEPS {
  login = 'login',
  mfa = 'mfa',
  details = 'details',
  nomfa = 'nomfa',
  warning = 'warning',
}

export enum MFA {
  NOMFA = 'NOMFA',
}

export const REGEX = {
  EMAIL: /^\w+([.\-+]?\w?)*@\w+([.-]?\w+)*(\.\w{2,10})+$/i,
  AWS_DECORATORS: /@aws_[a-z_]+/gim,
  NUMBERS: /\D/g,
  ASTERISK: /\*/g,
  COLON: /:/g,
  HYPHEN: /-/g,
  FORWARD_SLASH: /\//g,
  DOUBLE_QUOTES: /"/g,
  ESCAPED_DOUBLE_QUOTES: /\\"/g,
  INCLUDES_SPACE: /^\S*$/,
  REPLACE_BRACKETS_AND_TEXT: /\[.*\]$/,
  ESCAPED_PERIODS: /\\./g,
  USER_ROLE: /^[A-Za-z\s_-]+$/,
};

export const URLS = {
  CORELIGHT_BLOG: 'https://corelight.com/blog',
  CORELIGHT_SUPPORT: 'https://corelight.com/support',
  CORELIGHT_PRIVACY_POLICY: 'https://corelight.com/privacy-policy/',
  INVESTIGATOR_DOCS: '/docs/index.html',
  GENERIC_HTTP_DOCS: '/docs/alerts/generic-alert-export.html',
  DEFENDER_DOCS: '/docs/settings/ms-defender.html',
  CROWDSTRIKE_DOCS: '/docs/settings/crowdstrike-edr.html',
  SERVICENOW_DOCS: '/docs/settings/service-now-settings.html',
  GPT_DOCS: '/docs/settings/gpt-settings.html',
  SPLUNK_DOCS_ENABLE_HTTP_EVENT_COLLECTOR: '/docs/alerts/splunk-inv-siem.html',
  ELASTIC_SEARCH_DOCS_HTTP_EXPORTERS: '/docs/alerts/elastic-inv-siem.html',
  LOG_SCALE_DOCS: '/docs/alerts/crowdstrike-inv-siem.html',
  ALERT_CATALOG_SEARCH:
    '/docs/settings/alert-catalog.html#search-the-alert-catalog',
  SHODAN: 'https://www.shodan.io/host',
  CENSYS: 'https://search.censys.io/hosts',
  VIRUS_TOTAL: 'https://www.virustotal.com/gui/ip-address',
  SPLUNK_HEC_URL_FORMAT:
    '/docs/alerts/splunk-inv-siem.html#step-2-configure-the-alert-exporter-in-investigator',
  PALO_ALTO_DOCS: '/docs/settings/palo-alto.html',
};

/**
 * External service logo paths
 */
export const EXTERNAL_SERVICE_LOGOS = {
  VIRUS_TOTAL: '/assets/images/logo-virustotal.png',
  SHODAN: '/assets/images/logo-shodan.png',
  CENSYS: '/assets/images/logo-censys.png',
} as const;

/**
 * MITRE ATT&CK color priority level gradient (green to dark red)
 */
export const MITRE_COLORS_PRIORITY_RANGE = [
  '#2C7A7B',
  '#1F7F89',
  '#118497',
  '#0E83A2',
  '#1B78A9',
  '#286EAF',
  '#3F60B5',
  '#5752BC',
  '#7345BD',
  '#9C43A9',
  '#C54094',
  '#D93F7A',
  '#DF3E5C',
  '#C53030',
] as const;

export const dateRangeOptions: DateSelectOption[] = [
  [1, 'hour'],
  [3, 'hours'],
  [12, 'hours'],
  [24, 'hours'],
  [3, 'days'],
  [7, 'days'],
  [2, 'weeks'],
  [1, 'month'],
  [3, 'months'],
];

export enum ActionType {
  PUBLISH = 'PUBLISH',
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
}

export enum Features {
  alertExport = 'alertExport',
  integrations = 'integrations',
  licenseDashboard = 'licenseDashboard',
}

export enum ExportServiceType {
  SPLUNK = 'splunk',
}

export enum ErrorCode {
  UNAUTHORIZED = 401,
  ACCESS_DENIED = 403,
  NOT_FOUND = 404,
  SERVER_ERROR = 500,
}

export enum momentJSDateFormats {
  D_MMM_HH_mm = 'D MMM HH:mm',
}

export enum expireWarningDays {
  EVAL_WARNING = 7,
  FIRST_WARNING = 30,
  SECOND_WARNING = 60,
}

export const graphqlErrorRedirects: ExtendedType = {
  loginRoute: ROUTES.login,
  notFoundRoute: ROUTES.notFound,
  serverErrorRoute: ROUTES.serverError,
};

export const FETCH_AUDIT_LOGS_LIMIT = 30;

export enum licenseType {
  STANDARD_EVAL = 'STANDARD_EVAL',
  STANDARD = 'STANDARD',
  ADVANCED_EVAL = 'ADVANCED_EVAL',
  ADVANCED = 'ADVANCED',
}

export const DETECTION_AUTO_CLOSE_DURATION = 7;

export const DISABLE_SSO_CONFIRMATION_TEXT = 'confirm';

export const MAX_ASSIGNEE_LENGTH = 30;

export const TOTAL_API_KEY_DEFINITIONS_LIMIT = 20; // defined in investigator-integrations config

/**
 * Placeholder value used when data is not available or missing
 */
export const MISSING_DATA_PLACEHOLDER = '—';

/**
 * Default page size for data tables across the application
 * Matches the DataTable component's default from @gamma/ui/data-table
 */
export const DEFAULT_TABLE_PAGE_SIZE = 10;

/**
 * Threshold for triggering confirmation step in bulk action operations.
 * When selection count exceeds this value, users must confirm before proceeding.
 */
export const BULK_ACTION_CONFIRMATION_THRESHOLD = 50;
